import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const FlagIcon = ({ code, size = 'normal' }) => {
  const flags = {
    en: "https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg",
    es: "https://upload.wikimedia.org/wikipedia/commons/9/9a/Flag_of_Spain.svg",
    zh: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Flag_of_the_People%27s_Republic_of_China.svg",
    de: "https://upload.wikimedia.org/wikipedia/commons/b/ba/Flag_of_Germany.svg",
    fr: "https://upload.wikimedia.org/wikipedia/commons/c/c3/Flag_of_France.svg",
    he: "https://upload.wikimedia.org/wikipedia/commons/d/d4/Flag_of_Israel.svg",
    ar: "https://commons.wikimedia.org/wiki/File:Flag_of_the_United_Arab_Emirates.svg"
  };

  const src = flags[code];
  if (!src) return null;

  const dimensions = size === 'large' 
    ? { width: 28, height: 20 } 
    : { width: 24, height: 16 };

  return <img src={src} alt={code} width={dimensions.width} height={dimensions.height} style={{ objectFit: 'cover', borderRadius: 2 }} />;
};

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'zh', name: '中文' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'he', name: 'עברית' },
  { code: 'ar', name: 'العربية' },
];

const SettingsMenu = () => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (code) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          transition: 'all 0.2s ease',
          padding: '6px',
          color: 'rgba(255,255,255,0.88)',
          '&:hover': {
            transform: 'scale(1.1)',
            bgcolor: 'rgba(255,255,255,0.1)',
          },
        }}
        aria-controls={open ? 'settings-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        title={currentLanguage.name}
      >
        <Box 
          sx={{ 
            width: 28, 
            height: 20, 
            borderRadius: '3px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FlagIcon code={currentLanguage.code} size="large" />
        </Box>
      </IconButton>
      <Menu
        id="settings-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'settings-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 180,
            borderRadius: 2,
            mt: 1,
            overflow: 'visible',
            bgcolor: '#161b22',
            border: '1px solid rgba(99,102,241,0.2)',
            color: 'rgba(255,255,255,0.88)',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: '#161b22',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              border: '1px solid rgba(99,102,241,0.2)',
              borderBottom: 'none',
              borderRight: 'none',
            },
          },
        }}
      >
        {/* Language Selection - Direct list */}
        <Box sx={{ px: 2, py: 1, bgcolor: '#0d1117', borderRadius: '8px 8px 0 0' }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
            {t('language') || 'Language'}
          </Typography>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageSelect(lang.code)}
            selected={i18n.language === lang.code}
            sx={{ 
              py: 1,
              '&.Mui-selected': {
                bgcolor: 'rgba(99,102,241,0.18)',
              },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.07)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <FlagIcon code={lang.code} />
            </ListItemIcon>
            <ListItemText 
              primary={lang.name} 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                fontWeight: i18n.language === lang.code ? 600 : 400,
                color: i18n.language === lang.code ? '#ffffff' : 'rgba(255,255,255,0.8)',
              }}
            />
            {i18n.language === lang.code && (
              <CheckIcon fontSize="small" sx={{ color: '#818cf8' }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SettingsMenu;
